/**
 * Speaker Labels Component
 * 
 * Displays and manages speaker labels/badges with editing capabilities
 */

import {useState, useMemo} from 'react';
import {cn} from '../../utils/cn';

export interface SpeakerInfo {
  label: string;
  displayName?: string;
  color?: string;
  totalTime?: number;
  segmentCount?: number;
}

interface SpeakerLabelsProps {
  /**
   * Array of unique speaker labels found in the transcript
   */
  speakers: string[];
  
  /**
   * Map of speaker labels to display names
   * Key: speaker label (e.g., "SPEAKER_00")
   * Value: display name (e.g., "John Doe")
   */
  speakerNames?: Record<string, string>;
  
  /**
   * Callback when speaker name is edited
   * @param label - Speaker label
   * @param displayName - New display name
   */
  onSpeakerNameChange?: (label: string, displayName: string) => void;
  
  /**
   * Optional: Custom colors for each speaker
   */
  speakerColors?: Record<string, string>;
  
  /**
   * Whether editing is enabled
   */
  editable?: boolean;
  
  /**
   * Size variant
   */
  size?: 'sm' | 'md' | 'lg';
  
  /**
   * Additional className
   */
  className?: string;
}

/**
 * Generate a consistent color for a speaker label
 * Uses a hash of the label to generate a color
 */
function generateSpeakerColor(label: string): string {
  // Simple hash function
  let hash = 0;
  for (let i = 0; i < label.length; i++) {
    hash = label.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  // Generate a color from the hash
  // Use a palette of distinct colors
  const colors = [
    '#3B82F6', // blue
    '#10B981', // green
    '#F59E0B', // amber
    '#EF4444', // red
    '#8B5CF6', // purple
    '#EC4899', // pink
    '#06B6D4', // cyan
    '#84CC16', // lime
    '#F97316', // orange
    '#6366F1', // indigo
  ];
  
  const index = Math.abs(hash) % colors.length;
  return colors[index];
}

/**
 * Get speaker display name or fallback to label
 */
function getSpeakerDisplayName(
  label: string,
  speakerNames?: Record<string, string>
): string {
  return speakerNames?.[label] || label;
}

/**
 * Get speaker color (custom or generated)
 */
function getSpeakerColor(
  label: string,
  speakerColors?: Record<string, string>
): string {
  return speakerColors?.[label] || generateSpeakerColor(label);
}

/**
 * Speaker Badge Component
 */
interface SpeakerBadgeProps {
  label: string;
  displayName: string;
  color: string;
  editable?: boolean;
  size?: 'sm' | 'md' | 'lg';
  onEdit?: (label: string, newName: string) => void;
}

function SpeakerBadge({
  label,
  displayName,
  color,
  editable = false,
  size = 'md',
  onEdit,
}: SpeakerBadgeProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(displayName);

  const handleSave = () => {
    if (editValue.trim() && editValue !== displayName && onEdit) {
      onEdit(label, editValue.trim());
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(displayName);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5',
  };

  if (isEditing && editable) {
    return (
      <div className="inline-flex items-center gap-1">
        <input
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className={cn(
            'px-2 py-0.5 rounded border border-blue-500',
            'bg-white dark:bg-gray-800',
            'text-gray-900 dark:text-white',
            'focus:outline-none focus:ring-2 focus:ring-blue-500',
            sizeClasses[size]
          )}
          autoFocus
        />
      </div>
    );
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full font-medium',
        'border transition-colors',
        sizeClasses[size],
        {
          'cursor-pointer hover:opacity-80': editable,
        }
      )}
      style={{
        backgroundColor: `${color}15`, // 15 = ~8% opacity
        borderColor: color,
        color: color,
      }}
      onClick={() => editable && setIsEditing(true)}
      title={editable ? 'Click to edit speaker name' : undefined}
    >
      {/* Speaker icon/indicator */}
      <span
        className="w-2 h-2 rounded-full"
        style={{backgroundColor: color}}
      />
      <span>{displayName}</span>
    </span>
  );
}

/**
 * Speaker Labels Component
 */
export function SpeakerLabels({
  speakers,
  speakerNames,
  onSpeakerNameChange,
  speakerColors,
  editable = true,
  size = 'md',
  className,
}: SpeakerLabelsProps) {
  // Generate colors for speakers that don't have custom colors
  const allSpeakerColors = useMemo(() => {
    const colors: Record<string, string> = {...speakerColors};
    speakers.forEach((label) => {
      if (!colors[label]) {
        colors[label] = generateSpeakerColor(label);
      }
    });
    return colors;
  }, [speakers, speakerColors]);

  if (!speakers || speakers.length === 0) {
    return null;
  }

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      {speakers.map((label) => {
        const displayName = getSpeakerDisplayName(label, speakerNames);
        const color = getSpeakerColor(label, allSpeakerColors);

        return (
          <SpeakerBadge
            key={label}
            label={label}
            displayName={displayName}
            color={color}
            editable={editable}
            size={size}
            onEdit={onSpeakerNameChange}
          />
        );
      })}
    </div>
  );
}

/**
 * Single Speaker Badge (for use in transcript segments)
 */
interface SingleSpeakerBadgeProps {
  speaker: string;
  speakerNames?: Record<string, string>;
  speakerColors?: Record<string, string>;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function SingleSpeakerBadge({
  speaker,
  speakerNames,
  speakerColors,
  size = 'sm',
  className,
}: SingleSpeakerBadgeProps) {
  const displayName = getSpeakerDisplayName(speaker, speakerNames);
  const color = getSpeakerColor(speaker, speakerColors);

  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full font-medium',
        'border',
        sizeClasses[size],
        className
      )}
      style={{
        backgroundColor: `${color}15`,
        borderColor: color,
        color: color,
      }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{backgroundColor: color}}
      />
      <span>{displayName}</span>
    </span>
  );
}

